import { injectable, inject } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import { type IRpcDispatcher } from '@evercall/json-rpc-dispatcher';
import DITypes from '@evercall/json-rpc-dispatcher/DITypes';
import { ITransactionType } from '@evercall/domain/vo/TransactionType';
import { IDialInCountry } from '@evercall/domain/vo/DialInCountry';
import { IPlatform } from '@evercall/domain/vo/Platform';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';

/**
 * Reference data needed to populate the call form dropdowns.
 *
 * These three reads used to live on the evercall app's `EvercallRepository` but had no consumer
 * outside the call form, so they moved here wholesale. `EvercallRepository` stays in `apps/evercall`
 * with the reads that serve the public landing pages (pricing, currencies, global dial-ins) — keeping
 * it there avoids dragging its pricing/dial-in value objects into a dmPortal-facing package.
 */
@injectable()
class EvercallCallFormRepository {
  constructor(
    @inject(DITypes.WebServiceRpcDispatcher) private rpc: IRpcDispatcher,
  ) {}

  getTransactionTypes = async (): Promise<Array<ITransactionType>> => {
    const req = new Request(
      'evercall.transaction_types.view',
      {},
    );
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload.collection;
  };

  getDialInCountries = async (): Promise<Array<IDialInCountry>> => {
    const req = new Request(
      'evercall.dial_in_countries.view',
      {},
    );
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload.collection;
  };

  /**
   * Get video platform list for Evercall Webinar and Video Meetings
   */
  getPlatformList = async (): Promise<IPlatform[]> => {
    const req = new Request(
      'evercall.get_platforms',
    );
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload.collection;
  };
}

export default EvercallCallFormRepository;
